/**
 * Human-readable labels for borrower/application field paths. Used for
 * save-error toasts ("Couldn't save: Date of birth…") and validation summaries.
 */
const BORROWER_FIELD_LABELS: Record<string, string> = {
  firstName: "First name",
  lastName: "Last name",
  middleName: "Middle name",
  suffix: "Suffix",
  email: "Email",
  phone: "Phone",
  citizenship: "Citizenship",
  dateOfBirth: "Date of birth",
  ssn: "Social Security Number",
  hasBankruptcy: "Bankruptcy status",
  bankruptcyDate: "Bankruptcy date",
  maritalStatus: "Marital status",
  dependentsAgesYears: "Dependents",
  ethnicities: "Ethnicity",
  races: "Race",
  biologicalSex: "Biological sex",
  employmentStatus: "Employment status",
  addresses: "Address",
  nearestRelativeName: "Nearest relative name",
  nearestRelativeRelationship: "Nearest relative relationship",
  nearestRelativePhone: "Nearest relative phone",
  employmentHistory: "Employment",
  financialAccounts: "Financial accounts",
  additionalIncome: "Additional income",
  expenses: "Expenses",
};

const FIELD_DISPLAY_NAMES: Record<string, string> = {
  // Primary borrower
  ...Object.fromEntries(
    Object.entries(BORROWER_FIELD_LABELS).map(([k, v]) => [
      `primaryBorrower.${k}`,
      v,
    ]),
  ),
  // Co-borrower
  ...Object.fromEntries(
    Object.entries(BORROWER_FIELD_LABELS).map(([k, v]) => [
      `coBorrower.${k}`,
      v,
    ]),
  ),
  // Home
  "home.manufacturer": "Manufacturer",
  "home.modelName": "Model name",
  "home.modelYear": "Model year",
  "home.serialNumber": "Serial number",
  "home.squareFootage": "Square footage",
  "home.numberOfBedrooms": "Bedrooms",
  "home.numberOfBathrooms": "Bathrooms",
  "home.homeType": "Home sections",
  "home.isNew": "New home",
  "home.primaryUse": "Primary use",
  "home.occupancyType": "Occupancy type",
  // Land
  "land.landSituation": "Land situation",
  "land.propertyAddress": "Property address",
  "land.estimatedLandValue": "Estimated land value",
  "land.acreage": "Acreage",
  "land.useAsDownPayment": "Land as down payment",
  "land.downPaymentAmount": "Land down payment",
  "land.monthlyLandPayment": "Monthly land payment",
  "land.landlordName": "Landlord name",
  "land.leaseType": "Lease type",
  "land.monthlyLeasePayment": "Monthly site payment",
  "land.isRefinance": "Land refinance",
  "land.purchaseDateMonth": "Land purchase date",
  "land.purchaseDateYear": "Land purchase date",
  // Pricing
  "pricing.homePrice": "Home price",
  "pricing.downPaymentAmount": "Down payment",
  "pricing.downPaymentPercentage": "Down payment %",
  "pricing.downPaymentSource": "Down payment source",
  "pricing.loanAmount": "Loan amount",
  "pricing.upgradesCost": "Upgrades",
  "pricing.siteImprovementCost": "Site improvements",
  "pricing.yearlyPremium": "Insurance premium",
  "pricing.loanTerm": "Loan term",
  "pricing.interestRate": "Interest rate",
  // Section-level fallbacks for unmapped sub-fields
  home: "Home",
  land: "Land",
  pricing: "Pricing",
  primaryBorrower: "Borrower",
  coBorrower: "Co-borrower",
  lenders: "Lenders",
};

export function getFieldDisplayName(fieldPath: string): string {
  // Normalize array indices out (e.g. primaryBorrower.addresses.0.street → primaryBorrower.addresses)
  const normalized = fieldPath.replace(/\.\d+\b.*/g, "");
  if (FIELD_DISPLAY_NAMES[normalized]) return FIELD_DISPLAY_NAMES[normalized];
  // Section-level fallback for paths not individually mapped (e.g. lenders.0.lenderId → lenders)
  const section = normalized.split(".")[0] ?? normalized;
  return FIELD_DISPLAY_NAMES[section] ?? normalized;
}
